import IframeResizer from '@rio-cloud/iframe-resizer';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import type { UserProfile } from 'oidc-client-ts';
import { connect } from 'react-redux';
import { Navigate, NavLink, Route, Routes } from 'react-router';

import type { ConfigState } from '../../config';
import { getConfig } from '../../configuration/setup/configSlice';
import type { RootState } from '../../configuration/setup/store';
import { getIdToken } from '../../configuration/tokenHandling/tokenSlice';
import { ActivationsPage } from './activations/ActivationsPage';
import { initToggles } from './common/toggles/featureToggles';
import { DiscountPage } from './discount/DiscountPage';
import { FreeTrialPage } from './free-trial/FreeTrialPage';
import { BookingIntentPage } from './integrated-offer/BookingIntentPage';
import { PackageActivationPage } from './integrated-offer/PackageActivationPage';
import {
    ACTIVATIONS_ROUTE,
    BILLING_ROUTE,
    BOOKING_INTENT_ROUTE,
    FREE_TRIAL_ROUTE,
    INTEGRATED_OFFER_ROUTE,
    PACKAGE_ACTIVATION_ROUTE,
    UTILITIES_ROUTE,
} from './routes/routes';
import { UtilitiesPage } from './utilities/UtilitiesPage';

export interface Props {
    idToken: UserProfile;
    config: ConfigState;
}

const App = (props: Props) => {
    const { idToken, config } = props;
    const menuUrl = config.backend.MENU_SERVICE as string;
    const appNavigator = menuUrl ? (
        <div className='iframe-wrapper'>
            <IframeResizer
                title='Admin Menu'
                src={menuUrl}
                direction='both'
                scrolling='yes'
                className='iFrameResizer'
            />
        </div>
    ) : undefined;

    if (!idToken?.sub?.startsWith('azure-my-rio-de')) {
        window.location.href = 'https://home.rio.cloud';
    }
    // Initialize toggles
    initToggles();
    return (
        <ApplicationLayout>
            <ApplicationLayout.Header>
                <ApplicationHeader
                    label={'Marketplace Admin'}
                    homeRoute={<NavLink to='https://admin.marketplace.rio.cloud' />}
                    appNavigator={appNavigator}
                    actionBarItems={[]}
                />
            </ApplicationLayout.Header>
            <ApplicationLayout.Body>
                <NotificationsContainer />
                <Routes>
                    <Route path={ACTIVATIONS_ROUTE} element={<ActivationsPage />} />
                    <Route
                        path={INTEGRATED_OFFER_ROUTE}
                        element={<Navigate to={`/${PACKAGE_ACTIVATION_ROUTE}`} replace />}
                    />
                    <Route path={PACKAGE_ACTIVATION_ROUTE} element={<PackageActivationPage />} />
                    <Route path={BOOKING_INTENT_ROUTE} element={<BookingIntentPage />} />
                    <Route path={BILLING_ROUTE} element={<DiscountPage />} />
                    <Route path={FREE_TRIAL_ROUTE} element={<FreeTrialPage />} />
                    <Route path={UTILITIES_ROUTE} element={<UtilitiesPage />} />
                    <Route path='*' element={<Navigate to={`/${ACTIVATIONS_ROUTE}`} replace />} />
                </Routes>
            </ApplicationLayout.Body>
        </ApplicationLayout>
    );
};

const mapStateToProps = (state: RootState): Props => ({
    // biome-ignore lint/style/noNonNullAssertion: legacy
    idToken: getIdToken(state)!,
    config: getConfig(state),
});

export const AppContainer = connect(mapStateToProps)(App);
